import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';

import { useTranslation } from '@lib/useTypedTranslation';
import Card from '../../../../components/card/card';
import { InitialTableState, useTableReducer } from '../../../../components/data-table/lib';
import { List } from '../../../../components/list/list';
import { getFormatDataUsageToLocale } from '../../../../components/data-table/dataFormatters';
import { useUserSettingsContext } from '../../../../context/userSettings';
import { useWorldRequest } from '../../../../lib/useWorldRequest';
import { useWorldAction } from '../../../../lib/useWorldAction';
import { useWorldRoutes } from '../../../../routes/parts/allWorldRoutes';
import { getUserTimeZone } from '../../../../lib/timeZone';
import { formatDateAsOrdinalWithFullMonthAndYear } from '../../../../lib/ordinal';
import { convertFilterToQuery, IFilterResult } from '../../../../components/filters/filterLib';
import { FilterContext } from '@components/data-table/filters/context';
import {
  IPerformanceDataUsageDeviceRow,
  IPerformanceDataUsageDevicesListData,
  getCorePerformanceDataUsageDevicesList,
  exportCorePerformanceDataUsageDevicesListAction,
  DataUsageDeviceType
} from '../../../../services/core/performanceDataUsageDevices';
import { useLanguageAndLocaleContext } from '../../../../context/languageAndLocale';
import { useCorePerformanceDevicesColumns } from './useCorePerformanceDevicesColumns';
import { useCorePerformanceDevicesFilterOptions } from './filters/useCorePerformanceDevicesFilterOptions';
import useNormalizedHeaderButtons from '../../../../components/controls/normalizedHeaderButtons';
import { getCorePerformanceDevicesColumnFromFilterGroup, getInitialCorePerformanceDevicesFilter } from './corePerformanceEventDevices';

import './corePerformanceEventDevices.css';

interface IProps {
  type: DataUsageDeviceType,
  utc: number,
  homeLocationId?: string | null,
  group?: string | null,
  zoneId?: string | null,
  model?: string | null
}

const tableName = 'corePerformanceDataUsageDevicesList';
const defaultPageSize = 20;

const dataUsageTypeTranslationKey: Record<DataUsageDeviceType, string> = {
  mobile: 'MOBILE_DATA_USAGE',
  wifi: 'WIFI_DATA_USAGE',
  total: 'TOTAL_DATA_USAGE'
};

const dataUsageColumnTranslationKey: Record<DataUsageDeviceType, string> = {
  mobile: 'MOBILE_DATA_USAGE_COLUMN',
  wifi: 'WIFI_DATA_USAGE_COLUMN',
  total: 'TOTAL_DATA_USAGE_COLUMN'
};

export function CorePerformanceDataUsageDevices(props: IProps) {
  const { type, utc, homeLocationId, group, zoneId, model } = props;
  const timeZone = getUserTimeZone();
  const { t } = useTranslation(['translation', 'performance']);
  const routes = useWorldRoutes();
  const { tablePageSizes } = useUserSettingsContext();
  const { locale } = useLanguageAndLocaleContext();

  const eventName = t(dataUsageTypeTranslationKey[type] as any, { ns: 'performance' });
  const columnTitle = t(dataUsageColumnTranslationKey[type] as any, { ns: 'performance' });
  const date = formatDateAsOrdinalWithFullMonthAndYear(utc, timeZone);
  const title = t('PERFORMANCE_DEVICES_TITLE', { ns: 'performance', eventName, date });

  const columns = useCorePerformanceDevicesColumns({
    countColumnTitle: columnTitle,
    columnId: type,
    columnDataPath: type,
    valueProcessor: getFormatDataUsageToLocale(locale, true)
  });

  const initialTableState: InitialTableState = {
    initialRows: 4,
    limit: tablePageSizes?.[tableName] || defaultPageSize,
    sort: { column: columns.findIndex((c) => c.id === type), direction: 'desc', field: type },
    filter: getInitialCorePerformanceDevicesFilter(homeLocationId, group, model)
  };

  const [tableReducerProperties, tableReducerFunctions] = useTableReducer<IPerformanceDataUsageDeviceRow>(tableName, initialTableState);
  const [
    { offset, limit, sort, filter, data, isLoading },
    { onFilter, onDataLoaded }
  ] = [tableReducerProperties, tableReducerFunctions];

  const listFetcher = useCallback(() => {
    return getCorePerformanceDataUsageDevicesList({
      name: type,
      utc,
      timeZone,
      zoneId,
      limit,
      offset,
      sort: { field: sort.field, order: sort.direction },
      filter: convertFilterToQuery(filter)
    });
  }, [type, utc, timeZone, zoneId, limit, offset, sort, filter]);

  const exportCorePerformanceDevices = useWorldAction(exportCorePerformanceDataUsageDevicesListAction);
  const onExportClicked = async (): Promise<string> => {
    return exportCorePerformanceDevices({
      name: type,
      utc,
      timeZone,
      zoneId,
      limit: 10000,
      offset: 0,
      sort: { field: sort.field, order: sort.direction },
      filter: convertFilterToQuery(filter)
    });
  };

  const additionalHeaderButtons = useNormalizedHeaderButtons({
    export: {
      exportFunction: onExportClicked,
      filename: `${title}.csv`,
      disabled: isLoading || !data?.length
    }
  });

  const { availableFilters, filtersLoaded } = useCorePerformanceDevicesFilterOptions();

  const handleFiltersUpdated = (newFilter: IFilterResult) => {
    onFilter(newFilter);
  };
  const handleCancel = (): void => { /* no-op */ };

  const translationItems = {
    statusText: {
      key: 'DEVICE',
      ns: 'translation'
    },
    tableTitle: {
      text: title
    }
  };

  const breadcrumb = (
    <div data-id="core-performance-devices-breadcrumb" className="core-performance-devices-breadcrumb">
      <Link to={routes.core.performance} aria-label="Link to performance" className="core-performance-devices-breadcrumb-link">{t('PERFORMANCE', { ns: 'translation' })}</Link>
      <i className="fas fa-chevron-right" />
      <div className="core-performance-devices-event-name">{eventName}</div>
    </div>
  );

  return (
    <FilterContext.Provider value={{
      getColumnFromFilterGroup: getCorePerformanceDevicesColumnFromFilterGroup,
      filters: filter,
      filterOptions: { availableFilters, filtersLoaded },
      handleFiltersUpdated,
      handleCancel
    }}>
      <div>
        <div data-id="core-performance-devices" className="core-performance-list">
          <Card noPadding={true}>
            <List<IPerformanceDataUsageDeviceRow, IPerformanceDataUsageDevicesListData, 'totalCount', 'list', 'id'>
              dataId='core_performance_devices_list'
              dataTableEnabled={true}
              customHeader={false}
              countDataPath='totalCount'
              listDataPath='list'
              hasCheckboxes={false}
              hasFilters={true}
              hasSearch={false}
              translationItems={translationItems}
              subHeader={breadcrumb}
              columns={columns}
              fetcher={listFetcher}
              tableReducerFunctions={tableReducerFunctions}
              tableReducerProperties={tableReducerProperties}
              useRequestHook={useWorldRequest}
              options={{ rowIdDataPath: 'id' }}
              additionalHeaderButtons={additionalHeaderButtons}
            />
          </Card>
        </div>
      </div>
    </FilterContext.Provider>
  );
}
